/**
 * Center Carousel (carousel_peek) — desktop-only Flickity layer.
 * Isolated from media-gallery.js / ProductInfo; observes variant DOM + preview state.
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 990;
  var MOBILE_LAYOUT_MAX = 989;
  var RESIZE_DEBOUNCE_MS = 180;

  function isMobileLayout() {
    return window.innerWidth <= MOBILE_LAYOUT_MAX;
  }

  function isTabletLayout() {
    return window.innerWidth >= 750 && window.innerWidth <= MOBILE_LAYOUT_MAX;
  }

  /** @type {WeakMap<HTMLElement, object>} */
  var registry = new WeakMap();

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function getActiveSlideIndex(slides) {
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-active')) return i;
    }
    return 0;
  }

  function getSlideNodes(list) {
    var slider = list.querySelector('.flickity-slider');
    if (slider) {
      return Array.prototype.slice.call(slider.querySelectorAll('.product__media-item'));
    }
    return Array.prototype.slice.call(list.querySelectorAll('.product__media-item'));
  }

  function setActiveSlideClass(list, index) {
    getSlideNodes(list).forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
    });
  }

  function resizeFlickity(entry) {
    if (entry && entry.flkty) entry.flkty.resize();
  }

  /** Sync Flickity → variant featured slide (COLOR/SIZE change). Never reloadCells here. */
  function syncFlickityToVariant(entry, list) {
    if (!entry.flkty || entry.fromFlickity || entry.initializing) return;

    var idx = getActiveSlideIndex(getSlideNodes(list));
    if (idx >= 0 && entry.flkty.selectedIndex !== idx) {
      entry.flkty.select(idx, false, true);
    }
  }

  /** Variant changed DOM (prepend/remove slides) — full reload required. */
  function reloadFlickity(entry, list) {
    if (!entry.flkty || entry.initializing) return;
    entry.flkty.reloadCells();
    entry.flkty.resize();
    syncFlickityToVariant(entry, list);
  }

  function bindFlickityNavigation(flkty, list, entry) {
    flkty.on('change', function (index) {
      entry.fromFlickity = true;
      setActiveSlideClass(list, index);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          entry.fromFlickity = false;
        });
      });
    });
  }

  function finalizeInit(mediaGallery, list, entry) {
    if (entry.finalized) return;
    entry.finalized = true;
    entry.initializing = false;

    entry.fromFlickity = true;
    var idx = getActiveSlideIndex(getSlideNodes(list));
    if (idx > 0) entry.flkty.select(idx, false, true);
    entry.flkty.resize();

    requestAnimationFrame(function () {
      entry.fromFlickity = false;
      bindObservers(mediaGallery, list, entry);
      entry.flkty.resize();
    });
  }

  function teardown(mediaGallery) {
    var entry = registry.get(mediaGallery);
    if (!entry) return;

    if (entry.observers) {
      entry.observers.forEach(function (observer) {
        observer.disconnect();
      });
    }

    if (entry.flkty) {
      entry.flkty.destroy();
    }

    registry.delete(mediaGallery);
  }

  function bindObservers(mediaGallery, list, entry) {
    if (entry.observersBound) return;
    entry.observersBound = true;
    entry.observers = [];

    var slideRoot = list.querySelector('.flickity-slider') || list;

    var domObserver = new MutationObserver(function (mutations) {
      var needsReload = mutations.some(function (mutation) {
        return mutation.type === 'childList';
      });

      if (needsReload) {
        requestAnimationFrame(function () {
          reloadFlickity(entry, list);
        });
        return;
      }

      var classChanged = mutations.some(function (mutation) {
        return (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target.classList &&
          mutation.target.classList.contains('product__media-item')
        );
      });

      if (classChanged) {
        requestAnimationFrame(function () {
          syncFlickityToVariant(entry, list);
        });
      }
    });

    domObserver.observe(slideRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    entry.observers.push(domObserver);

    var previewObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'class') return;
        if (mediaGallery.classList.contains('is-b1-preview-active')) return;

        requestAnimationFrame(function () {
          resizeFlickity(entry);
          syncFlickityToVariant(entry, list);
        });
      });
    });

    previewObserver.observe(mediaGallery, {
      attributes: true,
      attributeFilter: ['class'],
    });
    entry.observers.push(previewObserver);
  }

  function initGallery(mediaGallery) {
    if (!isDesktop() || typeof Flickity === 'undefined') return;
    if (registry.has(mediaGallery)) return;

    var list = mediaGallery.querySelector('.product__media-list--carousel-peek');
    if (!list || list.classList.contains('flickity-enabled')) return;

    var slideCount = list.querySelectorAll('.product__media-item').length;
    if (slideCount < 1) return;

    var entry = {
      flkty: null,
      observers: [],
      observersBound: false,
      initializing: true,
      finalized: false,
      fromFlickity: false,
    };

    var flkty = new Flickity(list, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: slideCount > 1,
      wrapAround: false,
      adaptiveHeight: false,
      imagesLoaded: true,
      percentPosition: true,
      draggable: slideCount > 1,
      cellSelector: '.product__media-item',
    });

    entry.flkty = flkty;
    registry.set(mediaGallery, entry);

    bindFlickityNavigation(flkty, list, entry);

    flkty.on('ready', function () {
      finalizeInit(mediaGallery, list, entry);
    });

    if (list.classList.contains('flickity-enabled')) {
      finalizeInit(mediaGallery, list, entry);
    }

    flkty.on('settle', function () {
      if (!entry.initializing) resizeFlickity(entry);
    });

    list.addEventListener(
      'load',
      function (event) {
        if (event.target && event.target.tagName === 'IMG') {
          resizeFlickity(entry);
        }
      },
      true
    );

    window.addEventListener('load', function onWindowLoad() {
      resizeFlickity(entry);
      window.removeEventListener('load', onWindowLoad);
    });
  }

  function bootGallery(mediaGallery) {
    if (mediaGallery.dataset.desktopLayout !== 'carousel_peek') return;

    if (isDesktop()) {
      initGallery(mediaGallery);
    } else {
      teardown(mediaGallery);
    }
  }

  function bootAll() {
    if (!document.querySelector('.product--carousel_peek')) return;
    document.querySelectorAll('media-gallery[data-desktop-layout="carousel_peek"]').forEach(bootGallery);
    syncCarouselPeekMobileLayout();
    initCarouselPeekSliderProgress();
  }

  function syncCarouselPeekMobileLayout() {
    if (!document.querySelector('.product--carousel_peek')) return;

    var mediaGallery = document.querySelector('media-gallery.product__column-sticky[data-desktop-layout="carousel_peek"]');
    var mainContent = document.getElementById('MainContent');
    if (!mediaGallery || !mainContent) return;

    var originalParent = document.querySelector('.product--carousel_peek .product__media-wrapper');
    if (!originalParent) return;

    if (isMobileLayout()) {
      if (mainContent.previousElementSibling !== mediaGallery) {
        mainContent.insertAdjacentElement('beforebegin', mediaGallery);
      }
      return;
    }

    if (!originalParent.contains(mediaGallery)) {
      originalParent.appendChild(mediaGallery);
    }
  }

  function initCarouselPeekSliderProgress() {
    if (!isMobileLayout()) return;

    var mediaGallery = document.querySelector('media-gallery.product__column-sticky[data-desktop-layout="carousel_peek"]');
    var slider = mediaGallery ? mediaGallery.querySelector('.product__media-list--carousel-peek') : document.querySelector('.product--carousel_peek .product__media-list--carousel-peek');
    var progressBar = mediaGallery ? mediaGallery.querySelector('#progressBar, .progress-bar') : document.getElementById('progressBar');
    var slideCounter = mediaGallery ? mediaGallery.querySelector('#slideCounter, .slide-counter') : document.getElementById('slideCounter');
    if (!slider) return;

    var slides = slider.querySelectorAll('.product__media-item.slider__slide, .product__media-item');
    if (!slides.length) return;

    if (slider.dataset.b1PeekProgressBound === 'true') {
      updateLiveProgress();
      return;
    }
    slider.dataset.b1PeekProgressBound = 'true';

    var totalSlides = slides.length;
    var raf = null;

    function updateLiveProgress() {
      raf = null;
      totalSlides = slides && slides.length ? slides.length : totalSlides;
      if (!totalSlides || !Number.isFinite(totalSlides)) totalSlides = 1;

      var maxScroll = Math.max(1, slider.scrollWidth - slider.clientWidth);
      var progress = Math.max(0, Math.min(1, slider.scrollLeft / maxScroll));
      if (!Number.isFinite(progress)) progress = 0;

      var width = totalSlides <= 1 ? 100 : ((1 / totalSlides) * 100) + (progress * ((totalSlides - 1) / totalSlides) * 100);
      if (!Number.isFinite(width)) width = 100;

      var currentIndex = Math.round(progress * (totalSlides - 1));
      if (!Number.isFinite(currentIndex)) currentIndex = 0;

      if (progressBar) {
        progressBar.style.width = Math.max(0, Math.min(100, width)) + '%';
      }
      if (slideCounter) {
        slideCounter.textContent = (currentIndex + 1) + ' / ' + totalSlides;
      }
    }

    function requestProgressUpdate() {
      if (raf) return;
      raf = window.requestAnimationFrame(updateLiveProgress);
    }

    slider.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestProgressUpdate, { passive: true });
    updateLiveProgress();
  }

  var onResize = debounce(function () {
    document.querySelectorAll('media-gallery[data-desktop-layout="carousel_peek"]').forEach(function (mediaGallery) {
      if (isDesktop()) {
        if (!registry.has(mediaGallery)) {
          initGallery(mediaGallery);
        } else {
          var list = mediaGallery.querySelector('.product__media-list--carousel-peek');
          var entry = registry.get(mediaGallery);
          if (list && entry) {
            resizeFlickity(entry);
          }
        }
      } else {
        teardown(mediaGallery);
      }
    });
    syncCarouselPeekMobileLayout();
    initCarouselPeekSliderProgress();
  }, RESIZE_DEBOUNCE_MS);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  window.addEventListener('load', function () {
    syncCarouselPeekMobileLayout();
    initCarouselPeekSliderProgress();
  });

  setTimeout(function () {
    syncCarouselPeekMobileLayout();
    initCarouselPeekSliderProgress();
  }, 320);

  window.addEventListener('resize', onResize);

  document.addEventListener('shopify:section:load', function (event) {
    var mediaGallery = event.target.querySelector('media-gallery[data-desktop-layout="carousel_peek"]');
    if (mediaGallery) bootGallery(mediaGallery);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var mediaGallery = event.target.querySelector('media-gallery[data-desktop-layout="carousel_peek"]');
    if (mediaGallery) teardown(mediaGallery);
  });
})();
